import { defineStore } from 'pinia';
import { computed, h, reactive, toRaw } from 'vue';
import { Order } from '@/coreDomainsModule/entities/order';
import { OrderHeaderKey, TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { TableContentItem } from '@/corePagesModule/BaseTableContent/interface/tableContentItems';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { findOrderStatusOrNull, OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { simpleCopy } from '@/utils/deepCopy';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { i18n } from '@/i18n';
import { handleFileInput } from '../importOrderExcelManager';
import {
  openFailToSavedModal,
  openLoadingModal,
  openOkInformationModal,
  openSuccessToSaveModal,
} from '@/corePagesModule/components/modal/modalActions';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { convertUnixTimeToIDate } from '@/corePagesModule/BaseTableContent/utils/dateFormat';
import { IDate } from '@/coreDomainsModule/date';
import { IUnorderedArrivalState } from './interfaces/unorderedArrival';
import { orderTableStoreInitialValue } from '../constants/stateInitialValue';
import { diContainer } from '../../coreContainer/container';
import { UpdateOrderStatusUseCase } from '@/coreUseCasesModule/order/updateOrderStatusUseCase';
import { OrderService } from '../domains/service/orderService';
import { ReminderMailInfo } from '@/remindModule/entities/reminder';
import { updateReminder } from '@/businessRepository/reminderTable/driver';
import { buildOrderReminderBody } from '@/orderTableModule/constants/reminderMail';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { OrderGroupModalState, FIRM_ORDER_TABLE_TYPE } from '../constants';
import { OrderGroupService } from '../domains/service/orderGroupService';
import OrderGroupBulkChangeETAModalContent from '@/corePagesModule/components/OrderGroup/OrderGroupBulkChangeETAModalContent.vue';
import { OrderGroupFactory } from '@/coreDomainsModule/factories/OrderGroupFactory';
import OrderGroupInfoSection from '../../corePagesModule/components/OrderGroup/OrderGroupInfoSection.vue';
import { IOrderGroup } from '../../coreDomainsModule/entities/IOrderGroup';
import { DownloadOrderExcelUseCase } from '../useCases/outputOrderExcelUseCase';
import type { OrderTemplateDetailEntity } from '@/orderTemplateModule/domains/entities/orderTemplate';
import { useExportOrderModalStore } from '../components/exportModal/useExportOrderModalStore';
import { useTableFilter } from '@/corePagesModule/composables/useTableFilter';
import { loadPreferences, myPreferences, savePreferences } from '@/appLogic/view/preferences';
import { exportFm } from '@/appLogic/fileManager/export/exportFileManager';
import { hasAlertCount, showOrderImportSuccessModalContent } from '@/coreDomainsModule/translateExcelOrder';
import OrderGroupBulkChangeMultipleETAModalContent from '../components/orderGroup/OrderGroupBulkChangeMultipleETAModalContent.vue';

const t = i18n.t;

/**
 * 確定発注画面のStoreを管理する
 */
export const useOrderTableStore = defineStore('useUnorderedArrival', () => {
  const state = reactive<IUnorderedArrivalState>(orderTableStoreInitialValue());
  const exportOrderModalStore = useExportOrderModalStore();
  const { cachedFilterItems, bulkUpdateFilterInfos, getFilterItemsValue, initTableFilter } = useTableFilter(FIRM_ORDER_TABLE_TYPE);

  const updateOrderStatusUseCase = new UpdateOrderStatusUseCase();

  /**
   * stateの初期化
   */
  const $reset = () => {
    Object.assign(state, orderTableStoreInitialValue());
  };

  const checkedArrivalItems = computed(() => {
    return convertTableContentItemsToUnorderedArrival(state.tableContentItems.filter((item) => state.checkedIds.includes(item.displayId)));
  });

  /**
   * オフセットをリセットする
   */
  const resetOffset = () => {
    // pagingOptionsがnullableなのでこうしてます
    if (state.pagingOptions) {
      state.pagingOptions.offset = 0;
    }
  };

  /**
   * 発注の取得
   */
  const fetchItems = async () => {
    state.loadingState = {
      status: {
        type: 'loading',
      },
    };

    await initTableFilter();

    // ステータスフィルターの永続化対応：IndexedDBから読み込み
    await loadPreferences();
    const savedStatusFilter = myPreferences[FIRM_ORDER_TABLE_TYPE]?.orderStatusFilter;
    if (savedStatusFilter && Object.keys(savedStatusFilter).length > 0) {
      state.orderStatusFilterItem = savedStatusFilter;
    }

    // ソート設定の永続化対応：IndexedDBから読み込み
    const savedSortKey = myPreferences[FIRM_ORDER_TABLE_TYPE]?.sortKey;
    const savedIsAsc = myPreferences[FIRM_ORDER_TABLE_TYPE]?.isAsc;
    if (savedSortKey) {
      state.sortKey = savedSortKey;
    }
    if (savedIsAsc !== undefined) {
      state.isAsc = savedIsAsc;
    }

    // ページサイズ設定の永続化対応：IndexedDBから読み込み
    const savedPageSize = myPreferences[FIRM_ORDER_TABLE_TYPE]?.pageSize;
    if (savedPageSize) {
      if (state.pagingOptions) {
        state.pagingOptions.pageSize = savedPageSize;
      } else {
        state.pagingOptions = { ...initPagingOptions(savedPageSize), pageSize: savedPageSize };
      }
    }

    // UIのソート表示を更新
    if (savedSortKey) {
      state.tableContentProperties.contentProperties = state.tableContentProperties.contentProperties.map((property) => {
        property.sortOption.isAsc = property.key === savedSortKey ? savedIsAsc : true;
        return property;
      });
    }
    // 毎回リマインダー情報を取得
    try {
      state.reminderData = await diContainer.getReminderDataUseCase.execute();
    } catch (error) {
      console.error('リマインダー情報の取得に失敗しました:', error);
      // エラーが発生してもメイン処理は続行する
      state.reminderData = [];
    }

    await fetchOrderGroup();

    const useFilterItems = [...cachedFilterItems.value];
    if (state.orderGroupState.targetGroupName !== '') {
      const targetOrderGroupId = state.orderGroupState.groupList.find(
        (group) => group.orderGroupName === state.orderGroupState.targetGroupName
      )?.orderGroupId;
      if (targetOrderGroupId) {
        useFilterItems.push(OrderGroupService.getFilterContentItem(targetOrderGroupId));
      }
    }

    let sortKey;
    switch (state.sortKey) {
      case 'OrderDate':
        sortKey = 'createdOrderDate';
        break;
      case 'OrderDesiredTimeOfArrival':
        sortKey = 'desiredDeliveryDate';
        break;
      case 'OrderNumber':
        sortKey = 'orderNumber';
        break;
      case 'OrderEstimateTimeOfArrival':
        sortKey = 'estimatedTimeOfArrival';
        break;
      case 'OrderAmount':
        sortKey = 'orderAmount';
        break;
      case 'OrderOutputDate':
        sortKey = 'outputDate';
        break;
      case 'OrderLastUpdatedBy':
        sortKey = 'lastUpdatedUser';
        break;
      case 'supplierForecastResponsibleUser':
        sortKey = 'aggregateBusinessResponsibleUser';
        break;
      case 'OrderGroupName':
        sortKey = 'orderGroupName';
        break;
      case 'OrderInvoiceNumber':
        sortKey = 'invoiceNumber';
        break;
      case 'OrderDestinationName':
        sortKey = 'destinationName';
        break;
      case 'OrderIssuedNumber':
        sortKey = 'orderIssuedNumber';
        break;
      default:
        sortKey = state.sortKey;
    }

    const result = await diContainer.getUnorderedArrivalUseCase.Execute(
      false,
      state.pagingOptions,
      sortKey,
      state.isAsc,
      useFilterItems,
      state.orderStatusFilterItem
    );

    switch (result.status.type) {
      case 'success':
        // 発注情報のreminderMailDataIdをクリーニング
        const cleanupResult = await diContainer.cleanupOrderReminderMailIdUseCase.execute(result.status.data.result, state.reminderData);

        // クリーニングで更新が発生した場合、更新後の最新データを再取得する
        if (cleanupResult.ordersToUpdate.length > 0) {
          const refetch = await diContainer.getUnorderedArrivalUseCase.Execute(
            false,
            state.pagingOptions,
            state.sortKey,
            state.isAsc,
            cachedFilterItems.value,
            state.orderStatusFilterItem
          );
          if (refetch.status.type === 'success') {
            state.items = refetch.status.data.result;
            state.pagingOptions = refetch.status.data.pagingOptions;
            state.tableContentItems = convertUnorderedArrivalToTableContentItems(state.items);
            _createOrderAlert(state.items);
            if ((refetch.status.data.pagingOptions?.returnCount ?? 0) === 0) {
              state.loadingState.status.type = 'zeroMatch';
            } else {
              state.loadingState.status.type = 'success';
            }
          } else {
            state.loadingState = {
              status: { type: 'error', errorMessage: '更新後の再取得に失敗しました' },
            };
          }
        } else {
          // 更新がなければ、そのままクリーニング済みデータを採用
          state.items = cleanupResult.cleanedOrders;
          state.pagingOptions = result.status.data.pagingOptions;
          state.tableContentItems = convertUnorderedArrivalToTableContentItems(state.items);
          _createOrderAlert(state.items); // アラートをつける
          if ((result.status.data.pagingOptions?.returnCount ?? 0) === 0) {
            state.loadingState.status.type = 'zeroMatch';
          } else {
            state.loadingState.status.type = 'success';
          }
        }
        break;
      case 'error':
        state.loadingState = {
          status: {
            type: 'error',
            errorMessage: '取得に失敗しました',
          },
        };
        break;
    }

    state.editingOrderId = '';
  };

  /**
   * テーブルのデータを再取得する
   */
  const reloadTable = async () => {
    await fetchItems();
  };

  /**
   * Stateをリセットし、データを再取得する
   */
  const resetAndFetch = async () => {
    $reset();
    await fetchItems();
    state.renderingKey += 1;
  };

  /**
   * 未発注の取引情報をテーブル表示用の情報に変換する
   * @param unorderedArrivals - 未発注の取引情報の配列
   * @returns テーブル表示用の情報
   */
  const convertUnorderedArrivalToTableContentItems = (unorderedArrivals: Order[]): TableContentItem[] => {
    const displayTableContent: TableContentItem[] = [];
    unorderedArrivals.forEach((unorderedArrival) => {
      displayTableContent.push({
        displayId: unorderedArrival.orderId,
        isVirtualChild: false,
        ...unorderedArrival,
      });
    });
    return displayTableContent;
  };

  /**
   * tableContentに対象の発注飲みを更新する
   * @param order - 更新する発注情報
   */
  const updateOrderInTableContent = (order: Order) => {
    const targetIndex = state.tableContentItems.findIndex((item) => item.orderId === order.orderId);
    if (targetIndex === -1) {
      return;
    }
    state.tableContentItems[targetIndex] = {
      displayId: order.orderId,
      isVirtualChild: false,
      ...order,
    };
  };

  /**
   * テーブル表示用の情報を未発注の取引情報に変換する
   * @param tableContentItems - テーブル表示用の情報
   * @returns 未発注の取引情報
   */
  const convertTableContentItemsToUnorderedArrival = (tableContentItems: TableContentItem[]): Order[] => {
    const unorderedArrivals: Order[] = [];
    tableContentItems.forEach((tableContentItem) => {
      unorderedArrivals.push({
        ...tableContentItem,
      });
    });
    return unorderedArrivals;
  };

  /**
   * 発注のアラートを作成する
   * @param orders - 発注情報の配列
   */
  const _createOrderAlert = (orders: Order[]): void => {
    for (const orderItem of orders) {
      // 納期遅延しているかどうか（警告）
      const today: IDate = convertUnixTimeToIDate(new Date().getTime());
      const orderStatus = orderItem.orderStatus;
      const estimatedTimeOfArrivalDate: IDate = convertUnixTimeToIDate(new Date(orderItem.estimatedTimeOfArrival).getTime());

      // 納期の翌日になっても納入済、表示終了、または不明ステータス以外の場合
      if (
        !(orderStatus === OrderStatus.Delivered || orderStatus === OrderStatus.DisplayEnded || orderStatus === OrderStatus.Unknown) &&
        DateService.compareDates(today, estimatedTimeOfArrivalDate) > 0
      ) {
        orderItem.orderAlert.push('DeliveryDateWarning');
      }

      // 在庫0予想がある場合の警告（納入済、表示終了、または不明ステータス以外）
      if (
        !(orderStatus === OrderStatus.Delivered || orderStatus === OrderStatus.DisplayEnded || orderStatus === OrderStatus.Unknown) &&
        orderItem.isOutOfStockExpected
      ) {
        orderItem.orderAlert.push('OrderAmountWarning');
      }

      // 納期当日の場合の注意喚起（納入済、表示終了、または不明ステータス以外）
      if (
        !(orderStatus === OrderStatus.Delivered || orderStatus === OrderStatus.DisplayEnded || orderStatus === OrderStatus.Unknown) &&
        DateService.compareDates(today, estimatedTimeOfArrivalDate) === 0
      ) {
        orderItem.orderAlert.push('DeliveryDateInfo');
      }

      // 発注出力から1週間経過しても納期回答が来ていない場合の注意喚起
      const outputDate = orderItem.outputDate;
      if (outputDate != null) {
        const outputIDate: IDate = convertUnixTimeToIDate(new Date(outputDate).getTime());
        const oneWeekLater: IDate = DateService.getShiftedIYearMonthByDays(outputIDate.year, outputIDate.month, outputIDate.day, 7);
        if (
          !(orderStatus === OrderStatus.Delivered || orderStatus === OrderStatus.DisplayEnded || orderStatus === OrderStatus.Unknown) &&
          DateService.compareDates(today, oneWeekLater) >= 0
        ) {
          orderItem.orderAlert.push('DeliveryDateConfirmedInfo');
        }
      }

      for (const partialDelivery of orderItem.partialDeliveries) {
        if (partialDelivery.orderStatus !== OrderStatus.Delivered) {
          // 希望納期を超過している場合の警告
          const desiredDeliveryDate: IDate = convertUnixTimeToIDate(new Date(orderItem.desiredDeliveryDate).getTime());
          if (DateService.compareDates(today, desiredDeliveryDate) > 0) {
            partialDelivery.orderAlert.push('DeliveryDateWarning');
          }

          const partialDeliveryDate: IDate = convertUnixTimeToIDate(new Date(partialDelivery.deliveryDate).getTime());
          // 分納の納期当日の場合の注意喚起
          if (DateService.compareDates(today, partialDeliveryDate) === 0) {
            partialDelivery.orderAlert.push('DeliveryDateInfo');
          }
        }
      }
    }
  };

  /**
   * ページング変更時の処理
   * @param pagingOptions - ページングオプション
   */
  const onChangePaging = async (pagingOptions: IPagingOptions) => {
    state.pagingOptions = {
      pageSize: pagingOptions.pageSize,
      offset: pagingOptions.offset,
      totalResultsAvailable: pagingOptions.totalResultsAvailable,
      returnCount: pagingOptions.returnCount,
    };
    // ページサイズ設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (!myPreferences[FIRM_ORDER_TABLE_TYPE]) {
      myPreferences[FIRM_ORDER_TABLE_TYPE] = {};
    }
    myPreferences[FIRM_ORDER_TABLE_TYPE].pageSize = state.pagingOptions.pageSize;
    await savePreferences();
    await fetchItems();
  };

  /**
   * テーブルのデータをソートする
   * @param sortKey - ソートキー
   * @param isAsc - 昇順かどうか
   */
  const sortTableContentItems = async (sortKey: TableContentProperty, isAsc: boolean) => {
    console.log(`sortKey: ${sortKey}, isAsc: ${isAsc}`);
    state.sortKey = sortKey.key;
    state.isAsc = isAsc;
    // ソート設定の永続化対応：IndexedDBに保存
    await loadPreferences();
    if (myPreferences[FIRM_ORDER_TABLE_TYPE]) {
      myPreferences[FIRM_ORDER_TABLE_TYPE].sortKey = state.sortKey;
      myPreferences[FIRM_ORDER_TABLE_TYPE].isAsc = state.isAsc;
      await savePreferences();
    }
    state.tableContentProperties.contentProperties = state.tableContentProperties.contentProperties.map((property) => {
      property.sortOption.isAsc = property.key === sortKey.key ? isAsc : true;
      return property;
    });
    resetOffset();
    await fetchItems();
  };

  /**
   * 発注の編集を適用するためにAPIを叩く
   */
  const updateEditOrder = async () => {
    const editingOrder = state.items.find((order) => order.orderId === state.editingOrderId);
    if (!editingOrder) {
      throw new Error('Editing order not found');
    }
    // 発注グループ合計数量のバリデーション
    if (state.orderGroupState.needCheckTotalAmount) {
      const linkedOrderGroup = state.orderGroupState.groupList.find((group) => group.orderGroupId === editingOrder.orderGroupId);
      if (!linkedOrderGroup) {
        throw new Error('編集中の発注に紐づけられた発注グループが存在しません');
      }
      const editedOrderOriginalAmount = state.orderGroupState.originalOrderAmounts[state.editingOrderId];
      if (!editedOrderOriginalAmount) {
        throw new Error('元の発注情報がありません');
      }
      if (
        linkedOrderGroup.totalLinkedOrderAmount + editingOrder.orderAmount - editedOrderOriginalAmount >
        linkedOrderGroup.upperLimitQuantity
      ) {
        await showOrderAmountOverOrderGroupLimit(linkedOrderGroup, 0, editingOrder.orderAmount - editedOrderOriginalAmount);
        return;
      }
    }
    const updateResult = await openLoadingModal<'success' | 'orderGroupError' | 'updateConflict' | 'unknownError'>(
      t('保存中・・・'),
      async () => {
        if (state.orderGroupState.needChangeEstimatedTimeOfArrival) {
          const editingGroup = state.orderGroupState.groupList.find((group) => group.orderGroupId === editingOrder.orderGroupId);
          if (!editingGroup) {
            throw new Error('Editing order group not found');
          }

          const updateEta = editingOrder.estimatedTimeOfArrival;
          const updateData = OrderGroupFactory.create({ ...editingGroup, eta: updateEta });

          const editOrderGroupResult = await diContainer.updateOrderGroupUseCase.execute(editingGroup.orderGroupId, updateData);
          if (editOrderGroupResult.status.type === 'success' && editOrderGroupResult.status.data !== undefined) {
            editingOrder.updatedAt = editOrderGroupResult.status.data;
          } else {
            return 'orderGroupError';
          }
        }

        const result = await diContainer.updateOrderUseCase.Execute(editingOrder);

        switch (result.status.type) {
          case 'success':
            return 'success';
          case 'error':
            if (result.status.error.code === 'UPDATE_TIMING_CONFLICT') {
              return 'updateConflict';
            }
            return 'unknownError';
          default:
            return 'unknownError';
        }
      },
      []
    );

    switch (updateResult) {
      case 'success':
        await openSuccessToSaveModal();
        break;
      case 'orderGroupError':
        await openOkInformationModal(
          'error',
          t('保存に失敗しました。'),
          'OK',
          t('何らかの理由で保存に失敗しました。再度時間を置いて実行していただくか、それでも解決しない場合はお問い合わせください。')
        );
        break;
      case 'updateConflict':
        await openOkInformationModal(
          'error',
          t('保存に失敗しました。'),
          'OK',
          t('編集中に他のユーザーが上書きしたため、保存に失敗しました。')
        );
        break;
      default:
        await openOkInformationModal(
          'error',
          t('保存に失敗しました。'),
          'OK',
          t('何らかの理由で保存に失敗しました。再度時間を置いて実行していただくか、それでも解決しない場合はお問い合わせください。')
        );
        break;
    }
    const beforePagingOption = simpleCopy(state.pagingOptions ?? initPagingOptions(30));
    $reset();
    state.pagingOptions = beforePagingOption;
    await fetchItems();
    state.forcePagingNumber = 1 + beforePagingOption.offset / beforePagingOption.pageSize;
    state.renderingKey += 1;
  };

  /**
   * 発注のステータスを一括更新する
   * @param orderStatus - 発注ステータス
   */
  const updateOrderStatus = async (orderStatus: OrderStatus) => {
    const result = await updateOrderStatusUseCase.Execute(diContainer.orderRepository, state.checkedIds, orderStatus.toString());

    if (result.status.type === 'error') {
      throw new Error(result.status.error.message);
    }
  };

  /**
   * ステータスフィルターアイコンの表示切り替え
   * @param orderStatus -
   * @param isShow -
   */
  const toggleStatusFilterIcon = async (orderStatus: OrderStatus, isShow: boolean) => {
    state.orderStatusFilterItem = {
      ...state.orderStatusFilterItem,
      [orderStatus]: isShow,
    };

    // ステータスフィルターの永続化対応：IndexedDBに保存
    await loadPreferences();
    if (myPreferences[FIRM_ORDER_TABLE_TYPE]) {
      myPreferences[FIRM_ORDER_TABLE_TYPE].orderStatusFilter = toRaw(state.orderStatusFilterItem);
      await savePreferences();
    }

    // オフセットをリセットしてから表示内容を更新
    resetOffset();
    await fetchItems();
  };

  /**
   * フィルターモーダルの適用ボタンクリック時の処理
   * @param filterItems - フィルターモーダル内で選択された項目
   */
  const onAppliedFilterModal = async (filterItems: FilterContentItem[]) => {
    // フィルターモーダルの選択情報を更新
    await bulkUpdateFilterInfos(simpleCopy(filterItems));
    // オフセットをリセットしてから表示内容を更新
    resetOffset();
    await fetchItems();
  };

  /**
   * 発注の削除時、注意用のモーダルを表示する必要があるかどうかを判定する
   */
  const isNeedAttentionForDelete = (): boolean => {
    if (state.checkedIds.length === 0) {
      return false;
    }
    // 選択した発注について、発注ステータスが「承認済」以外のものが含まれている場合は注意用のモーダルを表示する
    return state.checkedIds.some((orderId) => {
      const targetOrder = state.items.find((order) => order.orderId === orderId);
      return targetOrder?.orderStatus !== OrderStatus.ApprovedAwaitingDeliveryDate;
    });
  };

  /**
   * 発注の削除を行う
   * @param deleteOrderIds - 削除する発注ID
   */
  const deleteOrders = async (deleteOrderIds: string[]) => {
    state.loadingState = {
      status: {
        type: 'loading',
      },
    };

    const result = await diContainer.bulkDeleteOrderUseCase.Execute(deleteOrderIds);

    switch (result.status.type) {
      case 'success':
        state.loadingState.status.type = 'success';
        toast.success(t('削除に成功しました。'));
        break;
      case 'error':
        state.loadingState = {
          status: {
            type: 'error',
            errorMessage: t('削除に失敗しました。'),
          },
        };
        toast.error(t('削除に失敗しました。'));
        break;
    }
  };

  /**
   * 発注をエクセルから登録/更新する
   * @param file - エクセルファイル
   */
  const bulkRegisterOrderExcel = async (file: File) => {
    const { splitOrderExcelData, isSuccess } = await handleFileInput(file);
    if (!isSuccess) {
      showInfoModal({
        modalTitle: t('エクセルファイルの読み込みに失敗'),
        modalDescription: t('エクセルファイルの読み込みに失敗しました。ファイルを確認し、再度お試しください。'),
      });
      return;
    }

    const result = await waitLoading(diContainer.bulkRegisterOrderBusinessUseCase.execute(splitOrderExcelData), t('更新中'));

    switch (result?.status.type) {
      case 'success':
        break;
      case 'error':
      default:
        state.registerOrderState = {
          status: {
            type: 'error',
            errorMessage: t('登録処理に失敗しました'),
          },
        };
        await openFailToSavedModal();
        return;
    }
    const countData = result.status.data;
    state.registerOrderState.status.type = 'success';
    await showInfoModal({
      modalTitle: t('発注取込完了'),
      modalDescription: t('発注を取り込みました。'),
      modalContent: showOrderImportSuccessModalContent([
        { data: countData.registered.updated, title: '更新済み' },
        {
          data: countData.registered.linkedOrderNumberByEtaAmount + countData.registered.linkedOrderNumberByIssued,
          title: '発注番号登録済み',
        },
        { data: countData.registered.unmatched, title: '未照合', link: '/unmatched-orders' },
      ]),
      backButtonLabel: t('OK'),
      isDisabledNextButton: true,
    });
    if (hasAlertCount(countData)) {
      await showInfoModal({
        modalTitle: t('一部の発注にアラートが出ています'),
        modalContent: showOrderImportSuccessModalContent([
          { data: countData.alerted.orderWithoutNumber, title: '発注番号未登録' },
          {
            data: countData.alerted.overdueOrder,
            title: '納期が今日以前の未納発注',
          },
          { data: countData.alerted.outOfStockOrder, title: '在庫ショート', isError: countData.alerted.outOfStockOrder > 0 },
        ]),
        backButtonLabel: t('OK'),
        isDisabledNextButton: true,
      });
    }
  };

  /**
   * 発注出力モーダルから出力を行った際の処理
   * @param file - 選択されたファイル（ファイル選択オプションの場合）
   */
  const exportOrder = async (file?: File) => {
    const selectedFormat = exportOrderModalStore.state.selectedFormat;
    if (selectedFormat === 'custom' && exportOrderModalStore.state.selectedTemplateId === '') {
      toast.error(t('出力書式を選択してください'));
      return false;
    }
    if (selectedFormat === 'file' && !file) {
      toast.error(t('出力ファイルを選択してください'));
      return false;
    }

    const templateId = selectedFormat === 'custom' ? exportOrderModalStore.state.selectedTemplateId : undefined;

    state.exportOrderModalState = false;

    let templateDetail: OrderTemplateDetailEntity | null = null;

    state.loadingState = {
      status: {
        type: 'loading',
      },
    };

    if (templateId) {
      const detailResult = await diContainer.getOrderTemplateDetailUseCase.Execute(templateId);
      if (detailResult.status.type !== 'success') {
        await openOkInformationModal(
          'error',
          t('エラー'),
          'OK',
          detailResult.status.error?.message ?? t('テンプレート詳細の取得に失敗しました')
        );
        return false;
      }
      templateDetail = detailResult.status.data;
    }

    // テンプレートエクセルファイルを取得する
    const result = await diContainer.outputOrderUseCase.Execute(state.checkedIds, templateId);
    if (result.status.type === 'error') {
      // その他のエラーの場合の処理
      // エラーモーダルを表示
      state.loadingState = {
        status: {
          type: 'error',
          errorMessage: '出力情報の取得に失敗しました',
        },
      };
      await openOkInformationModal('error', t('エラー'), 'OK', result.status.error.message);
      return false;
    }

    // 取得したデータを元に、受領用内示のエクセルをダウンロードする
    let exportResult: void | Error;
    if (selectedFormat === 'file' && file) {
      exportResult = await exportFm.exportOrderExcel(result.status.data.excelInputInfo, file);
    } else {
      const downloadExcelUseCase = new DownloadOrderExcelUseCase();
      exportResult = await downloadExcelUseCase.Execute(
        result.status.data.excelInputInfo,
        result.status.data.templateOrderExcelData,
        templateDetail
      );
    }
    if (exportResult instanceof Error) {
      state.loadingState = {
        status: {
          type: 'error',
          errorMessage: '発注エクセルの作成に失敗しました',
        },
      };
      toast.error(t(exportResult.message));
      return false;
    }

    // 発注フラグを立てる
    const isSuccessUpdatedOutputFlag = await updateOutputFlag();
    // 成功トースト表示
    if (isSuccessUpdatedOutputFlag) {
      toast.success(t('ダウンロードしました'));
    }
    return isSuccessUpdatedOutputFlag;
  };

  /**
   * 発注フラグを立てる
   * TODO: そもそも発注機能自体をバックエンドに持っていくので、そこで内包されて不要になったら消す
   */
  const updateOutputFlag = async (): Promise<boolean> => {
    const result = await diContainer.updateOutputFlagUseCase.Execute(state.checkedIds);

    switch (result.status.type) {
      case 'success':
        state.loadingState.status.type = 'success';
        toast.success(t('出力に成功しました。'));
        return true;
      case 'error':
        state.loadingState = {
          status: {
            type: 'error',
            errorMessage: '発注状態の更新に失敗しました',
          },
        };
        break;
    }
    return false;
  };

  /**
   * 発注に対して変更を加える
   * @param orderId - 発注ID
   * @param key - 変更対象のキー
   * @param value - 変更する値
   */
  const modifyOrder = (orderId: string, key: OrderHeaderKey, value: string) => {
    const order = state.items.find((order) => order.orderId === orderId);
    if (!order) {
      throw new Error('Order not found');
    }

    let before: number | undefined = undefined;
    let updated: number | undefined = undefined;

    /**
     * 変更可能な値は発注数量、発注番号、納期、ステータスの4種類
     */
    switch (key) {
      case 'OrderRecommendStatus':
        const status = findOrderStatusOrNull(value);
        if (order.orderGroupId && status === OrderStatus.PartialDelivery) {
          const originalOrderStatus = order.orderStatus;
          // modelValue更新のために一度更新してすぐ戻す
          order.orderStatus = status;
          order.orderStatus = originalOrderStatus;
          showInfoModal({
            modalTitle: t('ステータスを変更することができません'),
            modalDescription: t(
              '発注グループが紐づいている発注は分納にすることができません。分納に変更したい場合は、発注グループから外してください。'
            ),
            backButtonLabel: 'OK',
            icon: 'error',
            colorStyle: 'error',
            enableOutsideClick: false,
            isDisabledNextButton: true,
          });
          return;
        }
        OrderService.changeOrderStatus(order, status ?? OrderStatus.Unknown);
        break;
      case 'OrderEstimateTimeOfArrival':
        updated = new Date(value).getTime();
        before = order.estimatedTimeOfArrival;
        order.estimatedTimeOfArrival = updated;
        break;
      case 'OrderAmount':
        if (order.orderGroupId) {
          state.orderGroupState.needCheckTotalAmount = true;
        }
        order.orderAmount = Number(value) || 0;
        break;
      case 'OrderNumber':
        order.orderNumber = value;
        break;
    }

    updateOrderInTableContent(order);

    // 発注グループに関係するバリデーション
    if (before && updated) {
      const beforeInnerScope = before; // スコープを跨ぐとナローイングが死ぬのでここで再定義
      _checkOrderETAWithOrderGroup(updated).then((validateResult) => {
        if (!validateResult) {
          order.estimatedTimeOfArrival = beforeInnerScope;
          updateOrderInTableContent(order);
        }
      });
    }
  };

  /**
   * 分納に対して変更を加える
   * @param orderId - 発注ID
   * @param partialDeliveryId - 分納ID
   * @param key - 変更対象のキー
   * @param value - 変更する値
   */
  const modifyPartialDelivery = (orderId: string, partialDeliveryId: string, key: OrderHeaderKey, value: string) => {
    const order = state.items.find((order) => order.orderId === orderId);
    if (!order) throw new Error('Order not found');
    const partialDelivery = order?.partialDeliveries.find((pd) => pd.id === partialDeliveryId);
    if (!partialDelivery) throw new Error('Partial delivery not found');

    /**
     * 変更可能な値は発注数量、発注番号、納期、ステータスの4種類
     */
    switch (key) {
      case 'OrderRecommendStatus':
        const status = findOrderStatusOrNull(value);
        OrderService.changePartialDeliveryStatus(order, partialDeliveryId, status ?? OrderStatus.Unknown);
        break;
      case 'OrderEstimateTimeOfArrival':
        partialDelivery.deliveryDate = new Date(value).getTime();
        break;
      case 'OrderAmount':
        partialDelivery.orderAmount = Number(value) || 0;
        break;
      case 'OrderNumber':
        partialDelivery.partialDeliveryNumber = value;
        break;
    }

    updateOrderInTableContent(order);
  };

  /**
   * 分納を削除する
   * @param orderId - 発注ID
   * @param partialDeliveryId - 分納ID
   */
  const deletePartialDelivery = (orderId: string, partialDeliveryId: string) => {
    const order = state.items.find((order) => order.orderId === orderId);
    if (!order) throw new Error('Order not found');
    const partialDelivery = order?.partialDeliveries.find((pd) => pd.id === partialDeliveryId);
    if (!partialDelivery) throw new Error('Partial delivery not found');

    OrderService.deletePartialDelivery(order, partialDelivery);

    updateOrderInTableContent(order);
  };

  /**
   * 分納を追加する
   * @param orderId - 発注ID
   */
  const addPartialDelivery = (orderId: string) => {
    const order = state.items.find((order) => order.orderId === orderId);
    if (!order) throw new Error('Order not found');

    OrderService.addPartialDelivery(order);

    updateOrderInTableContent(order);
  };

  /**
   * 発注のメモを更新する
   * @param orderId - 発注ID
   * @param memo - メモ
   */
  const updateMemo = async (orderId: string, memo: string) => {
    // ローディング開始
    state.updateMemoState.workType = 'update';
    state.updateMemoState.loading.status.type = 'loading';

    // メモの更新
    const result = await diContainer.updateOrderMemoUseCase.Execute(orderId, memo);

    switch (result.status.type) {
      case 'success':
        state.updateMemoState.loading.status.type = 'success';
        toast.success(t('メモの更新に成功しました。'));
        // メモ更新に連動して、関連するリマインドメール本文を更新（失敗は握りつぶす）
        try {
          // 対象発注のreminderMailDataIdを取得
          const targetOrder = state.items.find((o) => o.orderId === orderId);
          const reminderId = targetOrder?.reminderMailDataId ?? null;

          if (reminderId) {
            // 既存のリマインダー情報を特定（なければスキップ）
            const existing = getExistingReminderData(reminderId);
            if (existing) {
              const mailBody = buildOrderReminderBody(memo);
              await updateReminder(existing.dataId, existing.date, existing.mailTo, existing.mailFrom, existing.title, mailBody);
              // ローカルキャッシュも更新しておく（画面上の一貫性向上用）
              const idx = state.reminderData.findIndex((r) => r.dataId === existing.dataId);
              if (idx >= 0) state.reminderData[idx] = { ...state.reminderData[idx], mailBody } as ReminderMailInfo;
            }
          }
        } catch (e) {
          // 失敗しても通知しない
          console.error('reminder mail body update failed (ignored):', e);
        }

        // テーブルデータをリロード
        await fetchItems();
        break;
      case 'error':
        state.updateMemoState.loading = {
          status: {
            type: 'error',
            errorMessage: t('メモの更新に失敗しました。'),
          },
        };
        toast.error(t('メモの更新に失敗しました。'));
        break;
    }
  };

  /**
   * 発注のメモを削除する
   * @param orderId - 発注ID
   */
  const deleteMemo = async (orderId: string) => {
    // ローディング開始
    state.updateMemoState.workType = 'delete';
    state.updateMemoState.loading.status.type = 'loading';
    // メモの削除
    const result = await diContainer.deleteOrderMemoUseCase.Execute(orderId);
    switch (result.status.type) {
      case 'success':
        state.updateMemoState.loading.status.type = 'success';
        toast.success(t('メモの削除に成功しました。'));
        // テーブルデータをリロード
        await fetchItems();
        break;
      case 'error':
        state.updateMemoState.loading = {
          status: {
            type: 'error',
            errorMessage: t('メモの削除に失敗しました。'),
          },
        };
        toast.error(t('メモの削除に失敗しました。'));
        break;
    }
  };

  /**
   * 既存のリマインダー情報を取得する
   * @param reminderMailDataId - リマインダーメールデータID
   * @returns リマインダー情報（存在しない場合はnull）
   */
  const getExistingReminderData = (reminderMailDataId: string | null): ReminderMailInfo | null => {
    if (!reminderMailDataId || state.reminderData.length === 0) {
      return null;
    }

    const existingData = state.reminderData.find((reminder) => reminder.dataId === reminderMailDataId);
    return existingData || null;
  };

  /**
   * 発注グループ作成
   */
  const onNextCreateOrderGroup = async () => {
    const result = await waitLoading(diContainer.postOrderGroupUseCase.execute(state.orderGroupState.editingGroup));
    if (!result) {
      await showInfoModal({
        modalTitle: t('エラー'),
        modalDescription: t('発注グループ作成中にエラーが発生しました。時間をおいて再度お試しください。'),
        backButtonLabel: 'OK',
        icon: 'error',
        colorStyle: 'error',
        enableOutsideClick: false,
        isDisabledNextButton: true,
      });
      return;
    }
    switch (result.status.type) {
      case 'success':
        showInfoModal({
          modalTitle: t('発注グループを作成しました'),
          backButtonLabel: 'OK',
          enableOutsideClick: false,
          isDisabledNextButton: true,
        });
        state.orderGroupState.openingModal = OrderGroupModalState.None;
        $reset();
        fetchItems();
        return;
      case 'error':
        if (result.status.error.code === 'RESOURCE_CONFLICT') {
          await showInfoModal({
            modalTitle: t('発注グループの名称が重複しています'),
            modalDescription: t('既存の名称と重複していないか確認してください。'),
            backButtonLabel: 'OK',
            icon: 'error',
            colorStyle: 'error',
            enableOutsideClick: false,
            isDisabledNextButton: true,
          });
          return;
        }
      default:
        await showInfoModal({
          modalTitle: t('エラー'),
          modalDescription: t('発注グループ作成中にエラーが発生しました。時間をおいて再度お試しください。'),
          backButtonLabel: 'OK',
          icon: 'error',
          colorStyle: 'error',
          enableOutsideClick: false,
          isDisabledNextButton: true,
        });
        return;
    }
  };

  /**
   * 発注グループを取得
   */
  const fetchOrderGroup = async () => {
    const result = await diContainer.getOrderGroupUseCase.execute();
    if (result.status.type === 'success') {
      if (
        state.orderGroupState.isOrderGroupEditMode &&
        !result.status.data.find((orderGroup) => orderGroup.orderGroupName === state.orderGroupState.targetGroupName)
      ) {
        $reset();
        fetchItems();
        return;
      }
      state.orderGroupState.groupList = result.status.data;
    }
  };

  const _targetOrderGroupId = computed(
    () => state.orderGroupState.groupList.find((group) => group.orderGroupName === state.orderGroupState.targetGroupName)?.orderGroupId
  );

  /**
   * 複数の発注を発注グループに紐づける
   * @param updateETA - 更新する納期
   * @returns 成功でtrue, それ以外はfalse
   */
  const postOrderToOrderGroupLinking = async (updateETA: number | undefined) => {
    if (!_targetOrderGroupId.value) {
      return false;
    }

    const result = await diContainer.bulkPostOrderToOrderGroupUseCase.execute(_targetOrderGroupId.value, state.checkedIds, updateETA);
    switch (result.status.type) {
      case 'success':
        return true;
      case 'error':
      default:
        return false;
    }
  };

  /**
   * 発注グループの削除処理
   * @returns true:処理成功/false:処理失敗
   */
  const onNextDeleteOrderGroup = async () => {
    if (_targetOrderGroupId.value) {
      const result = await waitLoading(diContainer.deleteOrderGroupUseCase.execute(_targetOrderGroupId.value));
      if (result && result.status.type === 'success') {
        resetAndFetch();
        await showInfoModal({
          modalTitle: t('発注グループを削除しました'),
          backButtonLabel: 'OK',
          enableOutsideClick: false,
          isDisabledNextButton: true,
        });
        return;
      }
    }

    await showInfoModal({
      modalTitle: t('エラー'),
      modalDescription: t('発注グループ削除中にエラーが発生しました。時間をおいて再度お試しください。'),
      backButtonLabel: 'OK',
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    return;
  };

  /**
   * 発注グループの編集処理
   * @param orderGroupName - 編集する発注グループの名前
   */
  const onNextEditOrderGroup = async (orderGroupName: string) => {
    const targetOrderGroupId = state.orderGroupState.groupList.find((group) => group.orderGroupName === orderGroupName)?.orderGroupId;
    if (targetOrderGroupId) {
      const result = await waitLoading(diContainer.updateOrderGroupUseCase.execute(targetOrderGroupId, state.orderGroupState.editingGroup));
      if (result && result.status.type === 'success') {
        fetchOrderGroup();
        await showInfoModal({
          modalTitle: t('発注グループの編集を保存しました'),
          backButtonLabel: 'OK',
          enableOutsideClick: false,
          isDisabledNextButton: true,
        });
        state.orderGroupState.openingModal = OrderGroupModalState.None;
        return;
      } else if (result?.status.type === 'error' && result.status.error.code === 'RESOURCE_CONFLICT') {
        await showInfoModal({
          modalTitle: t('発注グループの名称が重複しています'),
          modalDescription: t('既存の名称と重複していないか確認してください。'),
          backButtonLabel: 'OK',
          icon: 'error',
          colorStyle: 'error',
          enableOutsideClick: false,
          isDisabledNextButton: true,
        });
        return;
      }
    }

    await showInfoModal({
      modalTitle: t('エラー'),
      modalDescription: t('発注グループ編集中にエラーが発生しました。時間をおいて再度お試しください。'),
      backButtonLabel: 'OK',
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    return;
  };

  /**
   * 発注グループから外すボタン押下時の処理
   */
  const onNextOrderToOrderGroupUnlinking = async () => {
    if (_targetOrderGroupId.value) {
      const result = await waitLoading(diContainer.bulkDeleteOrderToOrderGroupUseCase.execute(_targetOrderGroupId.value, state.checkedIds));
      if (result && result.status.type === 'success') {
        state.checkedIds = [];
        fetchItems();
        await showInfoModal({
          modalTitle: t('発注グループから発注を外しました'),
          backButtonLabel: 'OK',
          enableOutsideClick: false,
          isDisabledNextButton: true,
        });
        return;
      }
    }

    await showInfoModal({
      modalTitle: t('エラー'),
      modalDescription: t('処理中にエラーが発生しました。時間をおいて再度お試しください。'),
      backButtonLabel: 'OK',
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    return;
  };

  /**
   * 発注を編集した際に発注グループの規則に従っているかをチェックする
   * @param updated - 変更後の値
   */
  const _checkOrderETAWithOrderGroup = async (updated: number) => {
    const editingOrder = state.items.find((order) => order.orderId === state.editingOrderId);
    if (!editingOrder) {
      throw new Error('編集中の発注が存在しません');
    }
    const linkedOrderGroup = state.orderGroupState.groupList.find((group) => group.orderGroupId === editingOrder.orderGroupId);
    if (!linkedOrderGroup) return true; // 発注グループなし
    if (linkedOrderGroup.eta !== updated) {
      // 納期のバリデーション
      const isConfirm = await showInfoModal({
        modalTitle: t('発注グループの納期を一括変更'),
        modalDescription: t(
          '選択した発注はグループ化されています。グループの納期も一括変更しますか？個別に納期を変更したい場合はグループから取り外してから納期変更してください。'
        ),
        modalContent: h(OrderGroupBulkChangeETAModalContent, {
          changedETA: updated,
          targetOrderGroup: linkedOrderGroup,
        }),
        backButtonLabel: t('キャンセル'),
        nextButtonLabel: t('納期一括変更'),
        enableOutsideClick: false,
        modalWidth: 1024,
      });
      if (isConfirm) {
        state.orderGroupState.needChangeEstimatedTimeOfArrival = true;
      }
      return isConfirm;
    } else {
      return true;
    }
  };

  /**
   * 発注グループの数量上限オーバーモーダルを表示する
   * @param targetOrderGroup - 対象の発注グループ
   * @param additionalOrderCount - 追加する発注の数
   * @param additionalTotalAmount - 追加される発注数量の合計
   * @returns - 数量上限オーバーモーダル表示関数
   */
  const showOrderAmountOverOrderGroupLimit = async (
    targetOrderGroup: IOrderGroup,
    additionalOrderCount: number,
    additionalTotalAmount: number
  ) =>
    await showInfoModal({
      modalTitle: t('発注グループの上限数量オーバー'),
      modalDescription: t('発注グループの上限数量の引き上げ、または各発注の数量を上限数量に収まるようにしてください。'),
      modalContent: h(OrderGroupInfoSection, {
        orderGroupName: targetOrderGroup.orderGroupName,
        numberOfOrder: targetOrderGroup.linkedOrderCount + additionalOrderCount,
        upperLimitQuantity: targetOrderGroup.upperLimitQuantity,
        totalAmount: targetOrderGroup.totalLinkedOrderAmount,
        afterTotalAmount: targetOrderGroup.totalLinkedOrderAmount + additionalTotalAmount,
        hasBackground: true,
      }),
      backButtonLabel: 'OK',
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
      modalWidth: 764,
    });

  /**
   * 発注のETAを一括更新する
   * @param updateEta - 更新するETA
   */
  const bulkUpdateOrderEta = async (updateEta: number) => {
    const targetOrders = state.items.filter((order) => state.checkedIds.includes(order.orderId));
    const orderGroupResult = await _checkOrdersETAWithOrderGroup(targetOrders, updateEta);
    if (!orderGroupResult) {
      return;
    }
    const updateResult = await openLoadingModal<'success' | 'orderGroupError' | 'updateConflict' | 'unknownError'>(
      t('保存中・・・'),
      async () => {
        const result = await diContainer.bulkUpdateOrderEtaUseCase.execute(targetOrders, updateEta, true);

        switch (result.status.type) {
          case 'success':
            return 'success';
          case 'error':
            if (result.status.error.code === 'UPDATE_TIMING_CONFLICT') {
              return 'updateConflict';
            }
            if (result.status.error.code === 'RESOURCE_CONFLICT') {
              return 'orderGroupError';
            }
            return 'unknownError';
          default:
            return 'unknownError';
        }
      },
      []
    );

    switch (updateResult) {
      case 'success':
        await openSuccessToSaveModal();
        break;
      case 'orderGroupError':
        await openOkInformationModal(
          'error',
          t('保存に失敗しました。'),
          'OK',
          t('何らかの理由で保存に失敗しました。再度時間を置いて実行していただくか、それでも解決しない場合はお問い合わせください。')
        );
        break;
      case 'updateConflict':
        await openOkInformationModal(
          'error',
          t('保存に失敗しました。'),
          'OK',
          t('編集中に他のユーザーが上書きしたため、保存に失敗しました。')
        );
        break;
      default:
        await openOkInformationModal(
          'error',
          t('保存に失敗しました。'),
          'OK',
          t('何らかの理由で保存に失敗しました。再度時間を置いて実行していただくか、それでも解決しない場合はお問い合わせください。')
        );
        break;
    }
    const beforePagingOption = simpleCopy(state.pagingOptions ?? initPagingOptions(30));
    $reset();
    state.pagingOptions = beforePagingOption;
    await fetchItems();
    state.renderingKey += 1;
  };

  /**
   * 発注納期を一括編集した際に、発注グループの規則に従っているかをチェックする
   * @param updatedOrders - 変更対象の発注IDリスト
   * @param updatedEta - 変更後のETA
   */
  const _checkOrdersETAWithOrderGroup = async (updatedOrders: Order[], updatedEta: number): Promise<boolean> => {
    const orderLinkedOrderGroup = updatedOrders.map((order) => order.orderGroupId).filter((id): id is string => id != null && id !== '');
    const linkedOrderGroupIds: Set<string> = new Set(orderLinkedOrderGroup);
    const invalidLinkedOrderGroups = state.orderGroupState.groupList.filter(
      (group) => linkedOrderGroupIds.has(group.orderGroupId) && group.eta !== updatedEta
    );
    if (!invalidLinkedOrderGroups.length) return true; // 紐づく発注グループなし
    // 納期のバリデーション
    const isConfirm = await showInfoModal({
      modalTitle: t('発注グループの納期を一括変更'),
      modalDescription: t(
        '選択した発注の中にグループに属している発注があります。グループの納期も一括変更しますか？個別に納期を変更したい場合はグループから取り外してから納期変更してください。'
      ),
      modalContent: h(OrderGroupBulkChangeMultipleETAModalContent, {
        changedETA: updatedEta,
        targetOrderGroups: invalidLinkedOrderGroups,
        changeOrderCount: updatedOrders.length,
        changeOrderWithGroupCount: orderLinkedOrderGroup.length,
      }),
      backButtonLabel: t('キャンセル'),
      nextButtonLabel: t('納期一括変更'),
      enableOutsideClick: false,
      modalWidth: 1024,
    });
    return isConfirm;
  };

  return {
    state,
    $reset,
    resetOffset,
    fetchItems,
    onChangePaging,
    sortTableContentItems,
    reloadTable,
    updateEditOrder,
    updateOrderStatus,
    toggleStatusFilterIcon,
    onAppliedFilterModal,
    isNeedAttentionForDelete,
    deleteOrders,
    bulkRegisterOrderExcel,
    exportOrder,
    modifyOrder,
    modifyPartialDelivery,
    deletePartialDelivery,
    addPartialDelivery,
    updateMemo,
    deleteMemo,
    getExistingReminderData,
    onNextCreateOrderGroup,
    postOrderToOrderGroupLinking,
    resetAndFetch,
    onNextDeleteOrderGroup,
    fetchOrderGroup,
    onNextEditOrderGroup,
    onNextOrderToOrderGroupUnlinking,
    showOrderAmountOverOrderGroupLimit,
    getFilterItemsValue,
    bulkUpdateOrderEta,
  };
});
